import { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Grid, Tooltip, Typography, IconButton } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import DataTable from "../../../components/features/Datatable";
import { useDispatch, useSelector } from "react-redux";
import Filter from "./Filter";
import moment from "moment";
import { getPreScreeningUserList, selectBmrPreScreening } from "../../../slices/feasibility"
import { hasAuthority } from "../../../../utils/authorities";
import { clinicalStatus, financialStatus } from "../../../../pages/appointment-details/common-pre-screen/common";
import { dateFormater } from "../../../utils/common/utils";
import DownloadImageApi from "../../../../services/downloadImageService"
import { EyeIconButton } from "../../../../assets/common-svg/CommonSvg"
import FilePreviewModal from "../../../../bmr/components/common/FilePreview"
import {capitalizeWords} from "../../../../utils/utils"

const FeasibilityAssessmentSubject = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const location = useLocation();
    const isFromPrescreenDashboard = location.state?.prescreen_dashboard === 1 ? 1 : 0;
    const isFromFeasibilityDashboard = location.state?.feasibility_dashboard === 1 ? 1 : 0;
    const isFromDashboard = isFromPrescreenDashboard === 1 || isFromFeasibilityDashboard === 1;
    const { bmr_permissions, bmr_submodules } = useSelector(state => state.loginData);
    const [openTooltips, setOpenTooltips] = useState({}); // Store open state for each tooltip
    const [previewOpen, setPreviewOpen] = useState(false);
    const [filePreview, setFilePreview] = useState("");
    const filterData = useMemo(() => {
        return bmr_submodules?.filter(op =>
            ["bmr-slots", "subjects", "apheresis-requests"].includes(op)
        );
    }, [bmr_submodules]);
    const allPreScreening = useSelector(selectBmrPreScreening)
    const filterConfig = {
        clinical_status: {
            label: "Clinical Status",
            enum: {
                1: 'Clinical Feasible',
                2: 'Feasible with Conditions',
                3: 'Non Feasible',
            },
},
        financial_status: {
            label: "Financial Status",
            enum: {
                1: 'Financial Feasible',
                2: 'Non Feasible',
                3: 'Special Approval',
                4: 'Not Applicable',
            },
        },
        type: {
            label: "Type",
            enum: {
                1: 'Trial',
                2: 'PMS',
                3: 'Commercial',
            },
        },
        apheresis_date: {
            label: "Apheresis Date",
        },
         cycle: {
        label: "Cycle", 
    },
         dashboardFilter: {
        label: "Dashboard Filter",
        enum: {
            true: "Active",
        }
    }
    }

    useEffect(() => {
        if (filterData?.length > 0) {
            navigate(`/bmr/feasibility-assessment/${filterData[0]}`, {
                state: {
                    prescreen_dashboard: isFromPrescreenDashboard ? 1 : 0,
                    feasibility_dashboard: isFromFeasibilityDashboard ? 1 : 0,
                },
            });
        }
    }, [filterData, navigate]);

    const updateFilter = (filter) => {
        fetchUsers(filter);
    }

    const fetchUsers = async (filter) => {
        const isDashboardFilterActive = filter && filter.dashboardFilter === true;

        const payload = {
            ...(filter || {}),
            prescreen_dashboard: isDashboardFilterActive ? isFromPrescreenDashboard : 0,
            feasibility_dashboard: isDashboardFilterActive ? isFromFeasibilityDashboard : 0
        };
        delete payload.dashboardFilter;

        await dispatch(getPreScreeningUserList(payload));
    }

    const handleDownload = async (id) => {
        try {
            const imgRes = await DownloadImageApi.getDownloadedImageul(id);
            const response = await fetch(imgRes?.data || "");
      
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            // Close all tooltips when download starts
            setOpenTooltips({});
             setFilePreview(response?.url);
             setPreviewOpen(true);
        } catch (error) {
            console.log("Download failed. Please try again later.");
        }
    };

    const handleTooltipOpen = (rowId, tooltipType) => {
        setOpenTooltips(prev => ({
            ...prev,
            [`${rowId}_${tooltipType}`]: true
        }));
    };

    const handleTooltipClose = (rowId, tooltipType) => {
        setOpenTooltips(prev => ({
            ...prev,
            [`${rowId}_${tooltipType}`]: false
        }));
    };

    const isTooltipOpen = (rowId, tooltipType) => {
        return openTooltips[`${rowId}_${tooltipType}`] || false;
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 0:
                return <Chip label="Pending" color="warning" />;
            case 1:
                return <Chip label="Clinical Feasible" color="success" />;
            case 2:
                return <Chip label="Not Feasible" color="error" />;
            case 3:
                return <Chip sx={{
                    backgroundColor: '#C3C3C3',
                    color: '#FFFFFF',
                }} label="Not Required" />;
            default:
                return <Chip label="N/A" color="default" />;
        }
    };

    const getFormattedDate = (date) => {
        return moment(date).format("DD/MM/YYYY");
    }

    const renderFileContent = (media)=>{
        return (
             media && media?.length > 0 ?
                         <Typography variant="body1" className="flex flex-col">
                            <span className="text-[#808080]">Files </span>
                        {media?.map((fileValues,keyIndex) =>(
                        <div  key={'key_'+keyIndex} className="flex mt-2 items-center justify-between bg-gray-100 p-2 rounded">
                          <span className="truncate max-w-xs">
                            {fileValues.file_name}
                          </span>
                          <div className="flex gap-2">
                            {fileValues?.id && (
                                  <Tooltip title="View">
                                    <span>
                                      <IconButton
                                        onClick={() => handleDownload(fileValues?.id)}
                                      >
                                        <EyeIconButton bgColor={"#3571FF"} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                           
                          </div>
                        </div>
                        )
                        )} </Typography>:''
        )
    }

    const renderTooltipContent = (row, statusType, feasibility, status, comment, title, approvedBy) => (
        <Box>
            <div className="p-3" style={{
                backgroundColor: feasibility[status]?.bgColor,
                borderTopRightRadius: '0.75rem',
                borderTopLeftRadius: '0.75rem',
            }}>
                <Typography variant="subtitle2" sx={{ color: feasibility[status]?.text, fontWeight: 'bold' }}>
                    {title}
                </Typography>
            </div>
            <div className="p-3 flex flex-col gap-4">
                <Typography variant="body1" className="flex gap-2">
                    <span className="text-[#808080]">Feasibility  </span>
                    <span>
                        {feasibility[status]?.lable}
                    </span>
                </Typography>
                <Typography variant="body1" className="flex gap-2">
                    <span className="text-[#808080]">Date  </span>{" "}
                    <span>
                        {row?.updated_at && dateFormater(row.updated_at)}
                    </span>
                </Typography>
                {title == "Financial Status" && row.payment_type && (
                    <Typography variant="body1" className="flex gap-2">
                        <span className="text-[#808080]">Payment Type  </span>{" "}
                        <span>
                         {row.payment_type ?? "- - -"}
                        </span>
                    </Typography>
                )}
                <Typography variant="body1" className="flex gap-2">
                    <span className="text-[#808080]">Approved By  </span>{" "}
                    <span>
                        {approvedBy || "--"}
                    </span>
                </Typography>
                <Typography variant="body1" className="flex flex-col">
                    <span className="text-[#808080]">Comment </span>
                    <span>{comment || "--"}</span>
                </Typography>
                {((row?.clinical_media && row?.clinical_media?.length > 0 && statusType === "clinical_status") || (row?.financial_media && row?.financial_media?.length > 0 && statusType === "financial_status")) ?
                renderFileContent(statusType == "clinical_status" ?  row?.clinical_media : row?.financial_media):''}
            </div>
        </Box>
    );

    // Helper to render status with optional tooltip
    const renderStatusWithTooltip = (row, statusType, statusMap, commentKey, title) => {
        const status = row[statusType];
        if (status === null || status === undefined) return "N/A";
        const label = statusMap[status]?.lable;
        const color = statusMap[status]?.color;
        const approvedBy = statusType === "clinical_status" ? row?.clinical_by_name : row?.financial_by_name;
        const tooltipTitle = renderTooltipContent(row, statusType, statusMap, status, row[commentKey], title, approvedBy);
        const tooltipKey = `${row.id}_${statusType}`;
        return (status == 0 ? <Chip className="cursor-pointer" label={label} color={color} /> :
            <Tooltip
                key={tooltipKey}
                title={tooltipTitle}
                arrow
                placement="top"
                enterDelay={200}
                open={isTooltipOpen(row.id, statusType)}
                onOpen={() => handleTooltipOpen(row.id, statusType)}
                onClose={() => handleTooltipClose(row.id, statusType)}
                componentsProps={{
                    tooltip: {
                        sx: {
                            padding: 0,
                            minWidth: 400,
                            borderRadius: '0.95rem',
                            bgcolor: 'white',
                            color: 'text.primary',
                            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.2)',
                            position: 'relative'
                        },
                        className: 'custom-tooltip',
                    },
                    arrow: {
                        sx: {
                            color: 'white',
                            '&:before': {
                                boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.2)'
                            }
                        },
                    },
                }}
                slotProps={{
                    popper: {
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, -8],
                                },
                            },
                        ],
                    },
                }}
            >
                <Chip className="cursor-pointer" label={label} color={color} />
            </Tooltip>
        );
    };

    const columns = [
        { id: "patient_id", name: "Subject/Patient ID", selector: row => row?.patient_id || "N/A", sortable: true },
        { id: "subject_name", name: "Subject/Patient Name", selector: row => row?.subject_name ? capitalizeWords(row?.subject_name) : "N/A", sortable: true },
        { id: "product", name: "Product/Phase", selector: row => (row?.product && row?.phase ? `${(row.product)}/${capitalizeWords(row.phase)}` : "N/A"), sortable: true },
        { id: "phase_type", name: "Type", selector: row => row?.phase_type || "N/A", sortable: true },
        { id: "hospital_name", name: "Hospital/Site", selector: row => row?.hospital_name || "N/A", sortable: true },
        {
            id: "clinical_status",
            name: "Clinical Status",
            selector: row => renderStatusWithTooltip(row, "clinical_status", clinicalStatus, "clinical_feasibility_comment", "Clinical Status"),
            sortable: true
        },
        {
            id: "financial_status",
            name: "Financial Status",
            selector: row => renderStatusWithTooltip(row, "financial_status", financialStatus, "financial_feasibility_comment", "Financial Status"),
            sortable: true
        },
        {
            id: "scheduled_apheresis_date",
            name: "Apheresis Date",
            selector: row => row?.scheduled_apheresis_date ? getFormattedDate(row.scheduled_apheresis_date) : getStatusChip(0),
            sortable: true
        },
        { id: "stage", name: "Stage", selector: row => row.stage ? row.stage : "N/A", sortable: true },

        {id: "cycleObject", 
    name: "Cycle",
    selector: (row) => {
        if (row.cycleObject) {
            const cycleValue =  row.cycleObject;
            return (
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium border border-blue-300 whitespace-nowrap">
                    {cycleValue}
                </span>
            );
        }
        return "N/A";
    },
    sortable: true,
},
        {
            name: "Action",
            selector: row => hasAuthority(['subjects-view-details'], bmr_permissions) && (
                <Button
                    onClick={() => navigate(`/bmr/feasibility-assessment/subjects/details/${row.subject_id}`,{
                        state: [
                            { name: "Feasibility Assessment", path: location.pathname },
                            {
                            name: 'Details',
                            path:`/bmr/feasibility-assessment/subjects/details/${row.subject_id}`,
                            },
                        ],
                    })}
                    key={row.id}
                    size="small"
                    className="!w-[100px]"
                >
                    View Details
                </Button>
            ),
        },
    ];
  useEffect(() => {
    fetchUsers({ dashboardFilter: isFromDashboard });
  }, [isFromPrescreenDashboard, isFromFeasibilityDashboard]); 
    return (
        <Grid container className="mt-4 ml-0 mr-3 mainDiv" rowSpacing={4.5} columnSpacing={2.75}>
            <Grid item xs={12} sx={{ mt: -2.25 }}>
                {hasAuthority(['subjects-listing'], bmr_permissions) &&
                    <DataTable
                        columns={columns}
                        data={allPreScreening ?? []}
                        title="Subject/Patient(Pre Screening Details)"
                        FilterComponent={Filter}
                        onServerSideChange={updateFilter}
                        remote={true}
                        filterConfig={filterConfig}
                        defaultFilters={isFromDashboard ? { dashboardFilter: true } : null}
                    />}
                      <FilePreviewModal
                            ModalTitle={"File Preview"}
                            setOpen={setPreviewOpen}
                            open={previewOpen}
                            fileURL={filePreview}
                          />
            </Grid>
        </Grid>
    );
    
};

export default FeasibilityAssessmentSubject;